import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { BlogRichTextEditor } from '@/features/admin/components/BlogRichTextEditor';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { blogApi, blogKeys } from '@/features/blogs/api/blog.api';

export function AdminBlogEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [contentHtml, setContentHtml] = useState('<p></p>');
  const [published, setPublished] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [editorReady, setEditorReady] = useState(!isEdit);

  const { data: existing, isLoading } = useQuery({
    queryKey: blogKeys.admin.detail(id ?? ''),
    queryFn: () => blogApi.getAdminById(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setTitle(existing.title);
    setSlug(existing.slug);
    setExcerpt(existing.excerpt);
    setCoverImage(existing.coverImage ?? '');
    setContentHtml(existing.contentHtml || '<p></p>');
    setPublished(existing.published);
    setEditorReady(true);
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim(),
        coverImage: coverImage.trim() || undefined,
        contentHtml,
        published,
      };
      if (isEdit && id) return blogApi.update(id, payload);
      return blogApi.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: blogKeys.admin.all });
      void queryClient.invalidateQueries({ queryKey: blogKeys.all });
      navigate(ADMIN_ROUTES.BLOGS);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save blog';
      setFormError(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!title.trim() || !excerpt.trim() || !contentHtml.trim() || contentHtml === '<p></p>') {
      setFormError('Title, excerpt, and content are required.');
      return;
    }
    saveMutation.mutate();
  };

  if (isEdit && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <AdminPageHeader
        title={isEdit ? 'Edit blog' : 'New blog'}
        subtitle="Rich text content with optional cover image"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => navigate(ADMIN_ROUTES.BLOGS)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saveMutation.isPending}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        }
      />

      {formError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormError(null)}>
          {formError}
        </Alert>
      )}

      <Stack spacing={2.5} sx={{ maxWidth: 860 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Slug (optional)"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          helperText="Leave blank to auto-generate from title"
          fullWidth
        />
        <TextField
          label="Excerpt"
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          required
          fullWidth
          multiline
          minRows={2}
        />

        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={uploadingCover}
              onClick={() => coverInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploadingCover ? 'Uploading…' : 'Upload cover image'}
            </Button>
            {coverImage && (
              <Button
                size="small"
                color="inherit"
                onClick={() => setCoverImage('')}
                sx={{ textTransform: 'none' }}
              >
                Remove
              </Button>
            )}
          </Stack>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/jpg"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setUploadingCover(true);
              try {
                const uploaded = await blogApi.uploadImage(file);
                if (uploaded?.url) {
                  setCoverImage(uploaded.url);
                } else {
                  setFormError('Cover image upload failed — no URL returned');
                }
              } catch (err) {
                const message =
                  (err as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message || 'Cover image upload failed';
                setFormError(message);
              } finally {
                setUploadingCover(false);
              }
            }}
          />
          {coverImage && (
            <Box
              component="img"
              src={coverImage}
              alt="Cover preview"
              sx={{
                mt: 1,
                width: '100%',
                maxHeight: 220,
                objectFit: 'cover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
        </Box>

        <FormControlLabel
          control={<Switch checked={published} onChange={(_, v) => setPublished(v)} />}
          label={published ? 'Published' : 'Draft'}
        />

        <Box>
          <Box sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>Content</Box>
          {editorReady && (
            <BlogRichTextEditor
              key={existing?.id ?? 'new-blog'}
              value={contentHtml}
              onChange={setContentHtml}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}
